import { supabase } from './supabase.js';
import { toCamel } from './case.js';

/**
 * Get the current user's active pair (if any).
 * Returns { pair, partner } or { pair: null, partner: null } if not paired.
 */
export async function fetchCurrentPair() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { pair: null, partner: null };

  // Find pair where current user is either user_a or user_b
  const { data: pairData, error: pairError } = await supabase
    .from('pairs')
    .select('*')
    .or(`user_a_id.eq.${user.id},user_b_id.eq.${user.id}`)
    .is('dissolved_at', null)
    .maybeSingle();

  if (pairError) {
    console.error('fetchCurrentPair error:', pairError);
    return { pair: null, partner: null };
  }

  if (!pairData) return { pair: null, partner: null };

  // Determine partner ID
  const partnerId = pairData.user_a_id === user.id ? pairData.user_b_id : pairData.user_a_id;

  // Fetch partner's profile
  const { data: partnerData } = await supabase
    .from('users')
    .select('*')
    .eq('id', partnerId)
    .single();

  return {
    pair: toCamel(pairData),
    partner: toCamel(partnerData),
  };
}

/**
 * Fetch pair stats (love score, streak, etc).
 */
export async function fetchPairStats(pairId) {
  if (!pairId) return null;
  const { data, error } = await supabase
    .from('pair_stats')
    .select('*')
    .eq('pair_id', pairId)
    .maybeSingle();
  if (error) {
    console.error('fetchPairStats error:', error);
    return null;
  }
  return toCamel(data);
}

/**
 * Update pair stats (typically called after task completion).
 * If no row exists yet, this creates one.
 */
export async function upsertPairStats(pairId, updates) {
  const { data, error } = await supabase
    .from('pair_stats')
    .upsert({ pair_id: pairId, ...updates }, { onConflict: 'pair_id' })
    .select()
    .single();
  if (error) throw error;
  return toCamel(data);
}
